import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getMakes, getVehicles, getBodyTypes } from "../api/cars";
import { BODY_TYPES as STATIC_BODY_TYPES } from "../data/bodyTypes"; // fallback, if you need it
import heroImg from "../assets/help me choose hero bg.png";

const MIN_PRICES = ["Any", "£5k", "£10k", "£15k", "£20k"];
const MAX_PRICES = ["Any", "£20k", "£30k", "£40k", "£50k"];

// Helper: convert "£5k" → 5000, or return undefined for "Any"
function parsePrice(str) {
  if (str === "Any") return undefined;
  const n = parseFloat(str.replace("£", "").replace("k", "")) * 1000;
  return isNaN(n) ? undefined : n;
}

// ── helper: turn "Up to 2 passengers" | "3 to 5 passengers" | "6 to 8 passengers"
//    into an array of seat-counts that we can send as ?seats[]=2&seats[]=3…
function parseSeats(passengers) {
  if (!passengers) return undefined;
  const nums = (passengers.match(/\d+/g) || []).map(Number);
  if (passengers.startsWith("Up to")) {
    // Up to X → seats = [1,2,…,X]
    return Array.from({ length: nums[0] }, (_, i) => i + 1);
  }
  if (passengers.includes("to")) {
    // "A to B" → seats = [A, A+1, …, B]
    const [min, max] = nums;
    return Array.from({ length: max - min + 1 }, (_, i) => min + i);
  }
  // fallback: exact
  return nums.length ? nums : undefined;
}

export default function FindYourPerfectCar() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    important: [],
    passengers: "",
    bodyTypes: [],
    priceMin: "Any",
    priceMax: "Any",
    makes: [],
  });
  const totalSteps = 6; // 0–4 questions, 5 = results

  // 1) Fetch available makes
  const {
    data: makes = [],
    isLoading: loadingMakes,
    isError: makesError,
  } = useQuery({
    queryKey: ["makes"],
    queryFn: getMakes,
    staleTime: 300_000,
  });

  // 2) Fetch dynamic body types (filtered by makes + price)
  const {
    data: bodyTypes = [],
    isLoading: loadingBodyTypes,
    isError: bodyTypesError,
  } = useQuery({
    queryKey: ["bodyTypes", answers.makes, answers.priceMin, answers.priceMax],
    queryFn: () =>
      getBodyTypes({
        make:
          answers.makes.includes("any") || answers.makes.length === 0
            ? undefined
            : answers.makes,
        minPrice: parsePrice(answers.priceMin),
        maxPrice: parsePrice(answers.priceMax),
      }),
    staleTime: 300_000,
    enabled: step >= 2, // start fetching by step 2
  });

  // 3) Fetch vehicles once on step 5
  const {
    data: vehiclesRes,
    isLoading: loadingVehicles,
    isError: vehiclesError,
  } = useQuery({
    queryKey: [
      "vehicles",
      answers.bodyTypes,
      answers.makes,
      answers.priceMin,
      answers.priceMax,
      answers.passengers,
    ],
    queryFn: () => {
      const filters = {
        bodyType:
          answers.bodyTypes.includes("any") || answers.bodyTypes.length === 0
            ? undefined
            : answers.bodyTypes,
        make:
          answers.makes.includes("any") || answers.makes.length === 0
            ? undefined
            : answers.makes,
        minPrice: parsePrice(answers.priceMin),
        maxPrice: parsePrice(answers.priceMax),
        seats: parseSeats(answers.passengers),
      };
      return getVehicles(filters);
    },
    enabled: step === 5,
    staleTime: 300_000,
  });

  // Validation per step
  const isValid = () => {
    switch (step) {
      case 0:
        return answers.important.length > 0 && answers.important.length <= 3;
      case 1:
        return !!answers.passengers;
      case 2:
        return answers.bodyTypes.length > 0;
      case 3:
        return true;
      case 4:
        return answers.makes.length > 0;
      default:
        return true;
    }
  };

  const next = () => {
    if (step < totalSteps - 1 && isValid()) setStep(step + 1);
  };
  const back = () => {
    if (step > 0) setStep(step - 1);
  };

  // Progress bar
  // const progressBar = () => {
  //   const pct = (step / (totalSteps - 1)) * 100;
  //   return (
  //     <div className="w-full h-2 bg-gray-200 rounded mb-8">
  //       <div
  //         className="h-full bg-cyan-400 rounded"
  //         style={{ width: `${pct}%` }}
  //       />
  //     </div>
  //   );
  // };

  // Reusable check icon
  const CheckIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5 text-purple-600"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );

  // Extract main + suggestions
  const vehicles = vehiclesRes?.data || [];
  const mainCar = vehicles[0];
  const suggestions = vehicles.slice(1, 5);

  return (
    <>
      {/* HERO SECTION */}
      <section className="relative w-full h-[60vh] md:h-[90vh] flex items-end text-white overflow-hidden z-0">
        {/* Background image */}
        <img
          src={heroImg}
          alt="Hero background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        {/* FULL OVERLAY tint instead of gradient */}
        <div
          className="absolute inset-0 z-10"
          style={{
            background:
              "linear-gradient(rgba(31, 41, 84, 0.5) 10%, rgba(239, 81, 75, 0.6) 77%)",
          }}
        />

        {/* Text content */}
        <div className="relative z-20 p-6 md:p-20">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-16">
            Help me <strong>Choose</strong>
          </h1>
        </div>
      </section>

      <div className="relative -mt-20 bg-primary px-6 md:px-40 lg:px-60 py-16 rounded-t-3xl -mb-60 pb-60 z-1">
        {/* {step < totalSteps - 1 && progressBar()} */}

        {/* STEP 0: Most important */}
        {step === 0 && (
          <>
            <h2 className="text-xl sm:text-2xl md:text-3xl mb-5 font-bold text-center">
              What's most important to you for your next car?
            </h2>
            <p className="text-center mb-6">Choose up to 3 options.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Compact and easy to park",
                "Safe for my family",
                "Good for long drives",
                "Space for all my stuff",
                "Fast and sporty",
                "Ready for off-road",
                "Luxurious inside and out",
                "Snazzy technology",
                "Better for the planet",
              ].map((opt) => {
                const sel = answers.important.includes(opt);
                return (
                  <button
                    key={opt}
                    onClick={() => {
                      let arr = [...answers.important];
                      if (sel) arr = arr.filter((x) => x !== opt);
                      else if (arr.length < 3) arr.push(opt);
                      setAnswers({ ...answers, important: arr });
                    }}
                    className={`relative overflow-hidden border p-4 text-left rounded transition ${
                      sel
                        ? "border-purple-600 bg-purple-50 text-primary"
                        : "border-gray-300 hover:border-purple-400"
                    }`}
                  >
                    {opt}
                    {sel && (
                      <div className="absolute top-2 right-2 bg-white rounded-full p-1">
                        <CheckIcon />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* STEP 1: Passengers */}
        {step === 1 && (
          <>
            <h2 className="text-3xl font-bold text-center">
              How many people will usually be in the car?
            </h2>
            <p className="text-center mb-6">Choose an option.</p>
            <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
              {[
                "Up to 2 passengers",
                "3 to 5 passengers",
                "6 to 8 passengers",
              ].map((opt) => {
                const sel = answers.passengers === opt;
                return (
                  <button
                    key={opt}
                    onClick={() => setAnswers({ ...answers, passengers: opt })}
                    className={`relative border p-4 rounded w-60 text-center transition ${
                      sel
                        ? "border-cyan-400 bg-cyan-50 text-primary"
                        : "border-gray-300 hover:border-cyan-400"
                    }`}
                  >
                    {opt}
                    {sel && (
                      <div className="absolute top-3 right-2 bg-white rounded-full p-1">
                        <CheckIcon />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* STEP 2: Dynamic Body Types */}
        {step === 2 && (
          <>
            <h2 className="text-3xl font-bold text-center mb-6">
              Which body types do you like?
            </h2>
            <p className="text-center mb-6">Choose at least one option.</p>

            {loadingBodyTypes && (
              <p className="text-center">Loading body types…</p>
            )}
            {bodyTypesError && (
              <p className="text-center text-red-500">
                Failed to load body types.
              </p>
            )}

            {!loadingBodyTypes && !bodyTypesError && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {bodyTypes.map(({ value, count }) => {
                  const sel = answers.bodyTypes.includes(value);
                  const normalize = (s) => s.replace(/\W+/g, "").toLowerCase();
                  const staticItem = STATIC_BODY_TYPES.find(
                    (b) =>
                      normalize(b.label) === normalize(value) ||
                      normalize(b.key) === normalize(value)
                  );

                  return (
                    <div
                      key={value}
                      onClick={() => {
                        let arr = [...answers.bodyTypes];
                        if (sel) arr = arr.filter((x) => x !== value);
                        else arr.push(value);
                        setAnswers({ ...answers, bodyTypes: arr });
                      }}
                      className={`relative border p-4 rounded cursor-pointer transition ${
                        sel
                          ? "border-purple-600 bg-purple-50"
                          : "border-gray-300 hover:border-purple-400"
                      }`}
                    >
                      {/* render the image */}
                      {staticItem && (
                        <img
                          src={staticItem.img}
                          alt={staticItem.label}
                          className="w-full h-40 object-cover rounded mb-3"
                        />
                      )}

                      <h3
                        className={`font-semibold ${
                          sel ? "text-purple-600" : ""
                        }`}
                      >
                        {value}
                      </h3>

                      {/* <p className="text-sm text-gray-500 mb-2">({count})</p> */}
                      {sel && (
                        <div className="absolute top-2 right-2 bg-white rounded-full p-1">
                          <CheckIcon />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* STEP 3: Price range */}
        {step === 3 && (
          <>
            <h2 className="text-3xl font-bold text-center mb-6">
              What price range are you looking in?
            </h2>
            <div className="flex flex-col md:flex-row items-center gap-8 justify-center">
              <div>
                <label className="block mb-2">Minimum price</label>
                <select
                  value={answers.priceMin}
                  onChange={(e) =>
                    setAnswers({ ...answers, priceMin: e.target.value })
                  }
                  className="border p-2 rounded w-48 text-primary"
                >
                  {MIN_PRICES.map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-2">Maximum price</label>
                <select
                  value={answers.priceMax}
                  onChange={(e) =>
                    setAnswers({ ...answers, priceMax: e.target.value })
                  }
                  className="border p-2 rounded w-48 text-primary"
                >
                  {MAX_PRICES.map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}

        {/* STEP 4: Makes (dynamic) */}
        {step === 4 && (
          <>
            <h2 className="text-3xl font-bold text-center mb-6">
              What car makes do you like?
            </h2>
            <p className="text-center mb-6">Choose at least one option.</p>

            {loadingMakes && <p className="text-center">Loading makes…</p>}
            {makesError && (
              <p className="text-center text-red-500">Failed to load makes.</p>
            )}

            {!loadingMakes && !makesError && (
              <>
                <button
                  onClick={() => setAnswers({ ...answers, makes: ["any"] })}
                  className={`relative w-full border p-4 rounded mb-6 transition ${
                    answers.makes.includes("any")
                      ? "border-purple-600 bg-purple-50 text-primary"
                      : "border-gray-300 hover:border-purple-400"
                  }`}
                >
                  I'm open to any
                  {answers.makes.includes("any") && (
                    <div className="absolute top-2 right-2 bg-white rounded-full p-1">
                      <CheckIcon />
                    </div>
                  )}
                </button>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {makes.map(({ value, count }) => {
                    const sel = answers.makes.includes(value);
                    return (
                      <button
                        key={value}
                        onClick={() => {
                          let arr = answers.makes.includes("any")
                            ? []
                            : [...answers.makes];
                          if (sel) arr = arr.filter((x) => x !== value);
                          else arr.push(value);
                          setAnswers({ ...answers, makes: arr });
                        }}
                        className={`relative border p-3 rounded text-left transition ${
                          sel
                            ? "border-purple-600 bg-purple-50 text-primary"
                            : "border-gray-300 hover:border-purple-400"
                        }`}
                      >
                        {value}
                        {sel && (
                          <div className="absolute top-2 right-2 bg-white rounded-full p-1">
                            <CheckIcon />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        {/* STEP 5: Results */}
        {step === 5 && (
          <>
            {loadingVehicles && <p className="text-center">Loading cars…</p>}
            {vehiclesError && (
              <p className="text-center text-red-500">Failed to load cars.</p>
            )}
            {!loadingVehicles && !vehiclesError && !mainCar && (
              <div className="flex flex-col justify-center items-center">
                <p className="text-center">
                  No cars found matching your filters.
                </p>
                <button
                  onClick={() => setStep(0)}
                  className="mt-8 px-6 py-2 border border-white rounded-full hover:text-accent"
                >
                  Change your answers
                </button>
              </div>
            )}
            {!loadingVehicles && !vehiclesError && mainCar && (
              <div className="space-y-8">
                {/* Main Recommendation */}
                <div className="bg-gray-50 p-10 rounded-lg text-center">
                  <h3 className="text-lg text-gray-600 font-bold">
                    We recommend
                  </h3>
                  <h2 className="text-2xl font-bold text-primary">
                    {mainCar.extended.extMake} {mainCar.extended.extModel}
                  </h2>
                  <p className="text-primary text-xl mb-10">
                    {mainCar.extended.bodyStyle}
                  </p>

                  {mainCar.base.thumbnail ? (
                    <img
                      src={mainCar.base.thumbnail}
                      alt={mainCar.base.modelDescription}
                      className="h-full w-96 object-contain mx-auto rounded-lg shadow mb-4"
                    />
                  ) : (
                    <img
                      src="https://zorqkblobstorage.blob.core.windows.net/zorqkblobstorage/Zorqk-coming-soon.jpg" // fallback image
                      alt="Default car"
                      className="h-full w-96 object-contain mx-auto rounded-lg shadow mb-4"
                    />
                  )}

                  <Link
                    to={`/car/${mainCar.base.id}`}
                    className="inline-block mt-2 bg-primary text-white px-6 py-2 rounded-full"
                  >
                    View details
                  </Link>
                </div>

                {/* Suggested Cars */}
                {suggestions.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold mb-2">
                      You might also like
                    </h3>
                    <p className="text-primary mb-6">
                      Not sure about the {mainCar.extended.extMake}{" "}
                      {mainCar.extended.extModel}? These scored well too.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                      {suggestions.map((c) => (
                        <div
                          key={c.base.id}
                          className="bg-white p-4 rounded-lg shadow text-center"
                        >
                          <div className="relative w-full h-32 mb-2">
                            {c.base.thumbnail ? (
                              <img
                                src={c.base.thumbnail}
                                alt={c.base.modelDescription}
                                className="w-full h-32 object-cover rounded"
                                onError={(e) => {
                                  e.currentTarget.src =
                                    "https://zorqkblobstorage.blob.core.windows.net/zorqkblobstorage/Zorqk-coming-soon.jpg";
                                }}
                              />
                            ) : (
                              <img
                                src="https://zorqkblobstorage.blob.core.windows.net/zorqkblobstorage/Zorqk-coming-soon.jpg" // fallback image
                                alt="Default car"
                                className="w-full h-32 object-cover rounded"
                              />
                            )}
                          </div>
                          <h4 className="font-medium text-primary">
                            {c.extended.extMake} {c.extended.extModel}
                          </h4>
                          <p className="text-primary text-sm">
                            {c.extended.bodyStyle}
                          </p>
                          <Link
                            to={`/car/${c.base.id}`}
                            className="mt-2 block text-center border border-primary text-primary hover:text-accent rounded-full py-1"
                          >
                            View details
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setStep(0)}
                  className="mt-8 px-6 py-2 border border-white rounded-full hover:text-accent"
                >
                  Change your answers
                </button>
              </div>
            )}
          </>
        )}

        {/* NAVIGATION BUTTONS */}
        {step < 5 && (
          <div className="mt-12 flex justify-between">
            <button
              onClick={back}
              disabled={step === 0}
              className="px-8 py-1 border border-accent rounded-full disabled:opacity-50"
            >
              Back
            </button>
            <button
              onClick={next}
              disabled={!isValid()}
              className="px-6 py-1 bg-accent text-white rounded-full disabled:opacity-50"
            >
              Continue
            </button>
          </div>
        )}
      </div>
    </>
  );
}
